// IngestEventSchema + PipelineMetaSchema — Phase 6 SSE contract hardening.
//
// Mirrors the TypeScript types from ingest/src/generate/stage-types.ts:
//   IngestEvent (9-variant discriminated union on `type`)
//   PipelineMeta
//
// Runtime validation for SSE events emitted by the ingest pipeline.
// Frontend staged-ingest-client.ts uses safeParse instead of `as IngestEvent`.
//
// IngestPackageSchema type erasure note:
//   IngestPackageSchema is annotated as `z.ZodObject<z.ZodRawShape>` (TS7056
//   avoidance). This does NOT affect runtime parse — it works correctly.
//   The pipeline-complete variant uses it as a field value (not a discriminator),
//   so z.discriminatedUnion works fine at runtime.
import { z } from 'zod';
import { IngestPackageSchema } from './ingest-package.js';
import { LintReportSchema } from './lint-report.js';
// ── PipelineMeta ─────────────────────────────────────────────────────────────
/** 19BO+ · P2-3 phase 3: structured record of one microloop sample gate's
 *  outcome. Mirrors MicroloopSignalRecord in stage-types.ts. */
export const MicroloopSignalRecordSchema = z.object({
    /** Whether any correction hint was injected into the rest batch. */
    fired: z.boolean(),
    /** Which signal categories triggered. Empty when fired=false. */
    signalTypes: z.array(z.string()),
    /** Chapter ids in the sample batch. */
    sampleChapterIds: z.array(z.string()),
    /** The merged hint text (undefined when fired=false). */
    hint: z.string().optional(),
    /** Distinct Bloom tiers observed across all sample-chapter items combined. */
    sampleTiersObserved: z.array(z.string()),
});
/** 19BO+ · P2-1: one route_discipline stage decision record. Mirrors
 *  RoutingDecisionRecord in stage-types.ts. */
export const RoutingDecisionRecordSchema = z.object({
    /** The scenario tag resolved (e.g. 'bar_exam', 'USMLE', 'IRAC'). 'default'
     *  when no examType signal and route fell back to generic. */
    scenario: z.string(),
    /** Where the scenario signal came from. */
    signalSource: z.enum(['explicit_exam_type', 'llm_inferred', 'default']),
    /** Rule keys from DISCIPLINE_FORMAT_RULES that this route activated.
     *  Empty = no discipline-specific rules apply for this scenario. */
    activatedRuleKeys: z.array(z.string()),
    /** LLM confidence 0-1 (undefined when signalSource='explicit_exam_type'
     *  because no LLM was needed, or when fallback). */
    confidence: z.number().min(0).max(1).optional(),
    /** Short LLM rationale for the routing decision (undefined on fallback). */
    rationale: z.string().optional(),
    /** ISO timestamp the route ran. Injected by handler post-LLM. */
    routedAt: z.string(),
    /** Model that produced the decision (undefined when signalSource='explicit_exam_type'
     *  zero-LLM path). */
    modelVersion: z.string().optional(),
    /** True when the LLM call failed/timed out and route fell back to P1-1
     *  static full-rule injection. False on a clean LLM or explicit-signal route. */
    fallback: z.boolean(),
});
export const PipelineMetaSchema = z.object({
    totalDurationMs: z.number(),
    stageDurations: z.record(z.string(), z.number()),
    inputTokens: z.number(),
    outputTokens: z.number(),
    providerName: z.string(),
    /** Stages that errored but pipeline recovered (skipped or empty output). */
    recoveredStages: z.array(z.string()),
    /** Stages that actually hit real LLM (for cost tracking). */
    llmStages: z.array(z.string()).optional(),
    /** Model each LLM stage used (for cost tracking). */
    modelByStage: z.record(z.string(), z.string()).optional(),
    /** 8-char FNV-1a hash of each stage's system prompt (prompt versioning). */
    promptVersions: z.record(z.string(), z.string()).optional(),
    /** Deterministic lint report (NOT model self-evaluation). */
    lintReport: LintReportSchema.optional(),
    /** Phase 3.5 Coordinator Synthesis advisory result. */
    coordinatorSynthesis: z.unknown().optional(),
    /** Stages that produced significantly fewer items than manifest min. */
    lowYieldStages: z.array(z.string()).optional(),
    /** 19BO+ · P0-2 (PIPELINE-META-METRICS): DeepSeek token breakdown.
     *  Mirrors PipelineMeta in stage-types.ts — see there for semantics. */
    reasoningTokens: z.number().optional(),
    cacheHitTokens: z.number().optional(),
    cacheMissTokens: z.number().optional(),
    finishReasons: z.record(z.string(), z.string()).optional(),
    /** 19BO+ · P2-3 phase 3: microloop early-signal observability. Mirrors
     *  PipelineMeta.microloopSignals in stage-types.ts. */
    microloopSignals: z.record(z.string(), MicroloopSignalRecordSchema).optional(),
    /** 19BO+ · P2-1: route_discipline stage decisions. Mirrors
     *  PipelineMeta.routingDecisions in stage-types.ts. */
    routingDecisions: z.array(RoutingDecisionRecordSchema).optional(),
});
// ── IngestEvent ──────────────────────────────────────────────────────────────
//
// 9 variants discriminated on `type`. Matches stage-types.ts IngestEvent.
export const IngestEventSchema = z.discriminatedUnion('type', [
    // 1. pipeline-start
    z.object({
        type: z.literal('pipeline-start'),
        stages: z.array(z.string()),
        total: z.number(),
    }),
    // 2. stage-start
    z.object({
        type: z.literal('stage-start'),
        stage: z.string(),
        index: z.number(),
        total: z.number(),
    }),
    // 3. stage-progress
    z.object({
        type: z.literal('stage-progress'),
        stage: z.string(),
        done: z.number(),
        total: z.number(),
        note: z.string().optional(),
    }),
    // 4. stage-skip
    z.object({
        type: z.literal('stage-skip'),
        stage: z.string(),
        reason: z.string(),
    }),
    // 5. stage-done
    z.object({
        type: z.literal('stage-done'),
        stage: z.string(),
        // StageOutput = Partial<IngestPackage>. Validation is z.record() not
        // IngestPackageSchema.partial() because stage output items may be
        // partially populated (e.g. missing optional fields like section/difficulty).
        // z.record() at least guarantees it's a key-value object, not a primitive.
        partial: z.record(z.string(), z.unknown()),
    }),
    // 6. stage-error
    z.object({
        type: z.literal('stage-error'),
        stage: z.string(),
        error: z.string(),
        recoverable: z.boolean(),
    }),
    // 7. fatal
    z.object({
        type: z.literal('fatal'),
        code: z.string(),
        error: z.string(),
    }),
    // 8. workflow-started (front-end synthetic — not from backend but from
    //    staged-ingest-client after POST /ingest/start returns)
    z.object({
        type: z.literal('workflow-started'),
        runId: z.string(),
        instanceId: z.string().optional(),
    }),
    // 9. pipeline-complete
    z.object({
        type: z.literal('pipeline-complete'),
        package: IngestPackageSchema,
        meta: PipelineMetaSchema,
    }),
]);
//# sourceMappingURL=ingest-event.js.map